<template>
	<div class="item-footer-container" :class="style()">
		<div class="item-detail-info-action">
			<div class="item-detail-info-action-secondary" v-if="!getBundle">
				<div class="item-action-quantity-wrapper">
					<BaseQuantityButtons
					:quantity="quantity"
					:settings="{ item: cartItem }"
					@incrementQuantity="increment()"
					@decrementQuantity="decrement()"
					@setInputQuantity="setQuantityFromInput" />
				</div>
			</div>
			<BaseButton
				title=""
				:ariaLabel="ariaLabelAddToCart"
				:classNames="addToCartButtonClasses()"
				:animated="animateButtonEnabled()"
				@click.native="addToOrder()"
				@afterAnimation="afterAddToCart">
				<div class="button-label-container">
					<span class="button-label truncate">{{addToOrderButtonTitle()}}</span>
					<span v-if="!_isOutOfStock" class="button-label">&dollar;{{displayFormattedPrice}}</span>
				</div>
			</BaseButton>
		</div>
	</div>
</template>

<script>
	import EventBus from '../mixins/eventBus';
	import Formatter from "@/utils/Formatter";
	import itemDetailMixin from '@Mixin/itemDetailMixin';
	import routeMixin from '@Mixin/routeMixin';
	import navigationMixin from '@Mixin/navigationMixin';
	export default {
		mixins: [
			routeMixin,
			itemDetailMixin,
			navigationMixin,
		],
		data() {
			return {
				addItemInProgress: false,
			};
		},
		computed: {
			ariaLabelAddToCart() {
				const title = this.addToOrderButtonTitle();
				const label = title.replace('$','').replace('-',' total : ');
				return label;
			},
			displayFormattedPrice() {
				const price = this.formattedPrice();
				if (price) {
					return price;
				}
				return '';
			},
		},
		created() {
			EventBus.$on('leavingEdit', this.handleLeave);
		},
		beforeDestroy() {
			EventBus.$off('leavingEdit', this.handleLeave);
		},
		methods: {
			handleLeave() {
				this.addToOrder();
			},
			// TODO Bundle related inqueries belong in bundle service
			addToOrderButtonTitle() {
				if (this._isOutOfStock) {
					return 'Unavailable';
				}
				const itemReady = `Add to Cart`;
				let title = itemReady;
				if (this.smallScreen) {
					title = `Add`;
				}
				if (this.getBundle) {
					if (this.disabledForBundle()) {
						title = `Choose items first`;
					} else if (this.isReadyForBundle()) {
						title = `Add special to cart`;
					}
				}
				if (this._isEditMode) {
					title = `Update`;
				}
				return title;
			},
			addToCartButtonClasses() {
				// TODO moving this to object syntax requires button component changes
				// Base button classNames should use object syntax
				// needs to be changed at the BaseButton level
				const classes = [
					'button--modifier',
					'addToCart',
				];
				const isDisabled = !this.validateItem();
				if (isDisabled) {
					classes.push('disabled');
				}
				return classes;
			},
			animateButtonEnabled() {
				if (this.getBundle) {
					return this.bundleReady;
				}
				return this.validOptions();
			},
			afterAddToCart() {
				if (!this.validateItem()) {
					return;
				}
				// Navigate back to landing page
				const router = this.$router;
				if (router.$hasPreviousRoute(router)) {
					router.go(-1);
					return;
				}
				this._navigateToHome();
			},
			formattedPrice() {
				let total = this.getTotal();
				const cartItem = this.cartItem;

				if (cartItem.freeChoices && !this.getBundle) {
					total = this.cartItem.getSingleItemTotal(this.cartItemOptions);
				}
				const discountedPrice = this.getDiscountedPrice;
				if (discountedPrice > 0) {
					total = discountedPrice;
				}
				if (this.editedBundle && this.getBundle) {
					this.updateBundleItemForEdit();
					const basePrice = this.bundle_getTotalFromBasePrice(this.editedBundle);
					total = basePrice;
				}
				return Formatter.centsToDollars(+total);
			},
			setQuantityFromInput(value) {
				this.setCurrentItemQuantity(value);
			},
			validOptions() {
				const requisites = this.validateCartOptions;
				const hasRequired = requisites.filter((option) => {
					return option.required;
				});
				if (this._isEditMode && hasRequired.length === 0) {
					return true;
				}
				if (requisites.length !== 0) {
					return false;
				}
				return true;
			},
			validateItem() {
				if (this._isOutOfStock) {
					return false;
				}
				if (!this.validOptions()) {
					return false;
				}
				if (this.getBundle && this.disabledForBundle()) {
					return false;
				}
				if (!this.validPrice()) {
					return false;
				}
				return true;
			},
			addToOrder() {
				if (!this.validateItem()) {
					// TODO this check happens twice, consider pattern for
					// better error handling
					if (this.getBundle && this.disabledForBundle()) {
						EventBus.$emit('alert-invalid-bundle');
					} else {
						EventBus.$emit('alert-invalid-item');
					}
					return;
				}
				let mode = null;
				if (this._isEditMode) {
					mode = 'updateItem';
				}
				EventBus.$emit('add-to-order', mode);
			},
			style() {
				return {
					'out-of-stock': this._isOutOfStock,
				};
			},
		},
	}
</script>

<style lang="scss" scoped>
	@import '@style-vars';
	.item-footer-container {
		position: sticky;
		bottom: 0;
		&.out-of-stock {
			@include out-of-stock;
		}
	}
	.item-detail-info-action {
		position: relative;
		display: flex;
		flex-direction: column-reverse;
		justify-content: space-between;
		align-items: center;

		padding-bottom: 50px;
		border-top: 1px solid $greyOutline;

		background-color: $landingPageBackground;

		min-width: 18rem;
		width: 100%;
		.item-action-quantity-wrapper {
			display: flex;
			justify-content: flex-end;

			margin-right: 0;
		}
		.item-detail-info-action-secondary {
			display: flex;
			justify-content: center;
		}
	}
	.button-label-container {
		display: flex;
		justify-content: space-between;
		width: 100%;
		.button-label {
			padding: 0 0.4em;
		}
	}
	@include desktop {
		.item-detail-info-action {
			padding-top: 2em;
			background-color: transparent;
			border: none;
			.item-detail-info-action-secondary {
				margin: 0 0 1rem;
			}
		}
	}
	@include mobile {
		.item-detail-info-action {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: center;

			.input--item-user-name {
				right: 0;
			}
			.button-wrapper {
				flex: 1;
				margin: 1em;
			}
			.item-action-quantity-wrapper {
				margin-right: 0;
				justify-content: center;
				border: 1px solid $greyOutline;
				border-radius: 4px;
				padding: 0.6em;
			}
			.item-detail-info-action-secondary {
				margin: 1rem 0;
				margin-left: 1em;
			}
		}
		.item-footer-container {
			z-index: $zTopInteractionLayer;
		}
	}
	@include tablet {}
</style>



// WEBPACK FOOTER //
// src/components/menu/ItemDetailActions.vue